import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { global_user } from '../global/global';

export type MigrationMode = 'legacy' | 'modern';

interface RagStatusResponse {
  userId: number;
  ragEnabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MigrationModeService {
  private readonly storageKey = 'migration_mode';
  private readonly baseUrl = environment.baseUrl;
  private modeSubject = new BehaviorSubject<MigrationMode>(this.getStoredMode());
  readonly mode$ = this.modeSubject.asObservable();

  constructor(private http: HttpClient) {}

  getCurrentMode(): MigrationMode {
    return this.modeSubject.value;
  }

  resolveModeForUser(userId: number): Observable<MigrationMode> {
    if (!Number.isFinite(userId) || userId <= 0) {
      return this.setModeAndReturn('legacy');
    }

    const token = localStorage.getItem('token');
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http
      .get<RagStatusResponse>(`${this.baseUrl}/users/rag-status`, {
        headers,
        params: { userId: String(userId) }
      })
      .pipe(
        map((response) => (response?.ragEnabled ? 'modern' : 'legacy') as MigrationMode),
        retry({
          count: 2,
          delay: (error) => {
            if (error?.status === 404) {
              return timer(250);
            }
            return throwError(() => error);
          }
        }),
        catchError(() => of('legacy' as MigrationMode)),
        tap((mode) => this.setMode(mode))
      );
  }

  resolveModeForCurrentUser(): Observable<MigrationMode> {
    const userId = this.resolveUserId();
    if (!userId) {
      return this.setModeAndReturn('legacy');
    }
    return this.resolveModeForUser(userId);
  }

  private setMode(mode: MigrationMode): void {
    this.modeSubject.next(mode);
    localStorage.setItem(this.storageKey, mode);
  }

  private setModeAndReturn(mode: MigrationMode): Observable<MigrationMode> {
    this.setMode(mode);
    return of(mode);
  }

  private getStoredMode(): MigrationMode {
    const mode = localStorage.getItem(this.storageKey);
    return mode === 'modern' ? 'modern' : 'legacy';
  }

  private resolveUserId(): number | null {
    const id = Number(global_user.user.id);
    if (Number.isFinite(id) && id > 0) {
      return id;
    }

    const cached = localStorage.getItem('userDetails');
    if (!cached) {
      return null;
    }

    try {
      const parsed = JSON.parse(cached);
      const fallbackId = Number(parsed?.user_id ?? parsed?.userId ?? parsed?.id);
      if (Number.isFinite(fallbackId) && fallbackId > 0) {
        return fallbackId;
      }
    } catch {
      return null;
    }

    return null;
  }
}
